// The Search (Library) singleton tab's centre: the library module's view
// (modules/library) over the host's projected pool, with the item views the
// host owns. Mounted only while the Search tab is active; the module itself
// lives in the shell's runtime, so the marked row and the opened item's
// content outlive a tab switch.

import { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect } from "react";
import { focusPagesOf } from "../../../components/Navigation/focusTarget.js";
import { cameFromStudio } from "../../../components/Navigation/studioReturn.js";
import type { FocusTarget } from "../../../components/Navigation/types.js";
import { runLibraryRowIntent } from "../../../components/Search/libraryRowIntent.js";
import { rankLibrary } from "../../../components/Search/searchModel.js";
import { LibraryView } from "../../../modules/library/view.js";
import { uploadingFilenames } from "../../ingest.js";
import { useAppShell } from "../context.js";
import { ResourceDetail } from "../ResourceDetail.js";
import { LIBRARY_KINDS } from "../resourceHome.js";
import { useLibrary } from "../useLibraryModule.js";
import { LibraryMaterial } from "./MaterialCentre.js";

// `query` is the active Search tab's text (`?q=`); absent ⇒ never typed since
// the tab opened, so it starts from the tab's `kind` (a rail category, a Home
// count-click). `selectedId` is the row gone into (`/library/{id}`), `target`
// where in it to open (a page a chat tool read).
export const SearchTabCentre = ({
    query,
    selectedId,
    target,
}: {
    query: string;
    selectedId?: string | undefined;
    target?: FocusTarget | undefined;
}): ReactNode => {
    const {
        ports,
        searchResults,
        openRef,
        draftMaterialFromSource,
        submitResourceRename,
        archiveResourceNow,
        unarchiveResourceNow,
        promoteFileNow,
        setConfirmDelete,
        requestUploadForSource,
        libraryMount,
        library,
        openFilePicker,
        ingest,
        dispatch,
        mkConversationId,
        submitText,
        frame,
    } = useAppShell();
    const { t } = useLingui();
    const model = useLibrary(libraryMount);
    const { send } = libraryMount;
    const pool = searchResults.filter((r) => LIBRARY_KINDS.includes(r.kind));
    const openedKind = pool.find((r) => r.id === selectedId)?.kind;
    // The words the query's content search runs (a typo-corrected word as
    // corrected, so the content agrees with the title list).
    const contentWords = rankLibrary(query, model.sort, pool).contentWords;
    const wordsKey = contentWords.join("\u0000");

    // Frame → module: the query and the opened row the tab shows; the module
    // starts the reads they imply.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `send` is stable; the opened row is keyed by id + kind, the words by their joined key.
    useEffect(() => {
        send({
            type: "shown",
            query,
            contentWords,
            opened:
                selectedId === undefined || openedKind === undefined
                    ? null
                    : { id: selectedId, kind: openedKind },
        });
    }, [query, wordsKey, selectedId, openedKind]);

    return (
        <LibraryView
            model={model}
            send={send}
            pool={pool}
            visibleKinds={LIBRARY_KINDS}
            query={query}
            openedId={selectedId ?? null}
            smartFilters={library?.smartFilters ?? []}
            // Nothing loaded yet: the shell shows while the server is
            // unreachable (the app never disappears).
            poolState={library === null ? "waiting" : "loaded"}
            // Opened on the Studio's canvas: back there (same zoom, pan and
            // selection).
            {...(cameFromStudio(frame.history)
                ? {
                      returnTo: {
                          label: t`Back to Studio`,
                          onReturn: () => dispatch({ type: "open-studio" }),
                      },
                  }
                : {})}
            onAddSource={openFilePicker}
            // "Create": a new conversation in the chat panel, the request its
            // first message (the same turn path the chat's own send takes).
            onCreate={(request) => {
                const conversationId = mkConversationId();
                dispatch({ type: "new-tab", conversationId });
                submitText(request, conversationId);
            }}
            uploading={uploadingFilenames(ingest)}
            // Each row intent runs its ONE lifecycle command (the same
            // handlers the rail's overflow uses).
            onRowIntent={runLibraryRowIntent({
                renameResource: submitResourceRename,
                archiveResource: archiveResourceNow,
                unarchiveResource: unarchiveResourceNow,
                // The shared confirm dialog; its confirm runs the delete.
                requestDeleteResource: setConfirmDelete,
            })}
            // The item gone into: the real per-resource views (source →
            // DetailWorkbench, package → ExportView, material → the in-place
            // editor), fed by the module's item read.
            renderItem={(result) => (
                <ResourceDetail
                    key={result.id}
                    result={result}
                    load={
                        model.detail._tag === "Detail.Open" &&
                        model.detail.id === result.id
                            ? model.detail.load
                            : Remote.loading
                    }
                    onRetry={() => send({ type: "detail_retried" })}
                    focusPages={focusPagesOf(target)}
                    // A material is EDITED in place (the editor host follows
                    // the opened material — editedMaterialRef).
                    renderMaterial={(material) => (
                        <LibraryMaterial
                            key={material.id}
                            title={material.title}
                            target={target}
                        />
                    )}
                    downloadExport={ports.downloadExport}
                    onOpenRef={openRef}
                    // F88 — the source's "Create material from this source".
                    onDraftFromSource={draftMaterialFromSource}
                    onReuploadSource={(source, onRestored) =>
                        requestUploadForSource(source.id, source.title, {
                            onRestored,
                        })
                    }
                    onPromoteFile={promoteFileNow}
                />
            )}
        />
    );
};
